/**
 * DynamicCamera — smoothly adjusts camera zoom based on proximity to
 * points of interest (POIs). When the player approaches a POI the camera
 * zooms out, revealing more of the surrounding area and signalling that
 * something interesting is nearby.
 *
 * POIs can come from tilemap spawn data (type "camera_poi") or be
 * registered programmatically (e.g. dungeon gates, NPC clusters).
 *
 * All tuning constants are imported from gameplay.config.ts.
 */

import {
  CAMERA_ZOOM_DEFAULT,
  CAMERA_ZOOM_POI,
  CAMERA_POI_OUTER_RADIUS,
  CAMERA_POI_INNER_RADIUS,
  CAMERA_ZOOM_LERP_SPEED,
  CAMERA_ZOOM_TRAVEL,
  CAMERA_ZOOM_SETTLED,
  CAMERA_TRAVEL_SPEED_THRESHOLD,
  CAMERA_TRAVEL_ONSET_SECS,
  CAMERA_TRAVEL_ZOOM_LERP_SPEED,
  CAMERA_ZOOM_TRAVEL_POI,
  CAMERA_ZOOM_MIN,
  CAMERA_ZOOM_HOME_ENTER,
  CAMERA_HOME_SETTLE_SECS,
} from '../config/gameplay.config';

// ── Types ──

export interface CameraPOI {
  /** Cartesian world-space X (pixels) */
  x: number;
  /** Cartesian world-space Y (pixels) */
  y: number;
  /** Override outer activation radius (px). Falls back to global default. */
  outerRadius?: number;
  /** Override inner full-effect radius (px). Falls back to global default. */
  innerRadius?: number;
  /** Override zoom level at full proximity. Falls back to global default. */
  zoom?: number;
  /** Optional label for debugging. */
  label?: string;
}

export class DynamicCamera {
  private pois: CameraPOI[] = [];
  private targetZoom: number = CAMERA_ZOOM_DEFAULT;
  private currentZoom: number = CAMERA_ZOOM_DEFAULT;
  private camera: Phaser.Cameras.Scene2D.Camera;

  // ── Travel zoom state ──
  private prevCartX: number = NaN;
  private prevCartY: number = NaN;
  /** Seconds the player has been moving above the travel speed threshold */
  private travelTimeSecs: number = 0;
  /** Seconds the player has been below the travel speed threshold */
  private settleTimeSecs: number = 0;
  /** Current travel zoom blend (0 = default, 1 = full travel zoom out) */
  private travelBlend: number = 0;

  // ── Home settle-zoom state (AGE-1178) ──
  /** Remaining seconds of the home-entry settle ease (0 = inactive) */
  private homeSettleRemaining: number = 0;

  constructor(camera: Phaser.Cameras.Scene2D.Camera) {
    this.camera = camera;
    this.camera.setZoom(CAMERA_ZOOM_DEFAULT);
  }

  // ── POI management ──

  /** Replace the entire POI list (e.g. when a new tilemap loads). */
  setPOIs(pois: CameraPOI[]): void {
    this.pois = pois;
  }

  /** Append POIs without clearing existing ones. */
  addPOIs(pois: CameraPOI[]): void {
    this.pois.push(...pois);
  }

  /** Remove all POIs. */
  clearPOIs(): void {
    this.pois = [];
  }

  // ── Per-frame update ──

  /**
   * Call once per frame from the scene's update().
   * @param playerCartX  Player's cartesian world X (px)
   * @param playerCartY  Player's cartesian world Y (px)
   * @param delta         Frame delta in ms
   */
  update(playerCartX: number, playerCartY: number, delta: number): void {
    const deltaSecs = delta / 1000;

    // ── Travel zoom: track velocity and accumulate travel / settle time ──
    if (!Number.isNaN(this.prevCartX)) {
      const dx = playerCartX - this.prevCartX;
      const dy = playerCartY - this.prevCartY;
      const speed = Math.sqrt(dx * dx + dy * dy) / deltaSecs; // px/s

      if (speed >= CAMERA_TRAVEL_SPEED_THRESHOLD) {
        this.travelTimeSecs += deltaSecs;
        this.settleTimeSecs = 0;
      } else {
        this.settleTimeSecs += deltaSecs;
        this.travelTimeSecs = 0;
      }
    }
    this.prevCartX = playerCartX;
    this.prevCartY = playerCartY;

    // Ramp travelBlend up when travelling, down immediately on stop
    if (this.travelTimeSecs >= CAMERA_TRAVEL_ONSET_SECS) {
      // Ramp up: how far past the onset threshold, normalized to onset duration
      const ramp = Math.min(1, (this.travelTimeSecs - CAMERA_TRAVEL_ONSET_SECS) / CAMERA_TRAVEL_ONSET_SECS);
      this.travelBlend = Math.min(1, Math.max(this.travelBlend, ramp));
    }
    if (this.settleTimeSecs > 0 && this.travelBlend > 0) {
      // Begin zooming back immediately on key release — no onset delay
      const settleLerp = 1 - Math.pow(1 - CAMERA_TRAVEL_ZOOM_LERP_SPEED, delta / 16.667);
      this.travelBlend -= this.travelBlend * settleLerp;
      if (this.travelBlend < 0.001) this.travelBlend = 0;
    }

    // Compute travel zoom target:
    //   blend 0 → CAMERA_ZOOM_DEFAULT
    //   blend 1 → CAMERA_ZOOM_TRAVEL (zoomed out)
    // When settling back (blend dropping), overshoot to CAMERA_ZOOM_SETTLED
    // (10% closer) then ease back to default.
    let travelZoomTarget: number;
    if (this.travelBlend > 0.01) {
      travelZoomTarget = CAMERA_ZOOM_DEFAULT + (CAMERA_ZOOM_TRAVEL - CAMERA_ZOOM_DEFAULT) * this.travelBlend;
    } else if (this.settleTimeSecs > 0 && this.settleTimeSecs < 3) {
      // Brief settle overshoot: zoom in slightly past default
      const overshootProgress = Math.min(1, this.settleTimeSecs / 3);
      travelZoomTarget = CAMERA_ZOOM_SETTLED + (CAMERA_ZOOM_DEFAULT - CAMERA_ZOOM_SETTLED) * overshootProgress;
    } else {
      travelZoomTarget = CAMERA_ZOOM_DEFAULT;
    }

    // ── POI zoom: find strongest influence ──
    let bestInfluence = 0;
    let bestZoom = CAMERA_ZOOM_POI;

    for (const poi of this.pois) {
      const pdx = playerCartX - poi.x;
      const pdy = playerCartY - poi.y;
      const distSq = pdx * pdx + pdy * pdy;

      const outer = poi.outerRadius ?? CAMERA_POI_OUTER_RADIUS;
      const inner = poi.innerRadius ?? CAMERA_POI_INNER_RADIUS;

      // Early-reject with squared distance to avoid sqrt when clearly outside
      if (distSq >= outer * outer) continue;

      const dist = Math.sqrt(distSq);
      const t = dist <= inner ? 1 : 1 - (dist - inner) / (outer - inner);

      if (t > bestInfluence) {
        bestInfluence = t;
        bestZoom = poi.zoom ?? CAMERA_ZOOM_POI;
      }
    }

    const poiZoomTarget = CAMERA_ZOOM_DEFAULT + (bestZoom - CAMERA_ZOOM_DEFAULT) * bestInfluence;

    // ── Compose: travel + POI ──
    // When both travel AND POI are active, super zoom out to capture the
    // sense of scale — showing the vastness of the journey and the
    // approaching destination together.
    if (this.travelBlend > 0.01 && bestInfluence > 0.01) {
      const combinedStrength = this.travelBlend * bestInfluence;
      const superZoomTarget = CAMERA_ZOOM_DEFAULT +
        (CAMERA_ZOOM_TRAVEL_POI - CAMERA_ZOOM_DEFAULT) * combinedStrength;
      this.targetZoom = Math.min(superZoomTarget, Math.min(travelZoomTarget, poiZoomTarget));
    } else {
      this.targetZoom = Math.min(travelZoomTarget, poiZoomTarget);
    }

    // ── Home settle-zoom overlay (AGE-1178) ──
    // Briefly eases from CAMERA_ZOOM_HOME_ENTER → CAMERA_ZOOM_DEFAULT on home entry.
    if (this.homeSettleRemaining > 0) {
      this.homeSettleRemaining = Math.max(0, this.homeSettleRemaining - deltaSecs);
      const t = 1 - this.homeSettleRemaining / CAMERA_HOME_SETTLE_SECS; // 0→1
      const smoothT = t * t * (3 - 2 * t); // smoothstep
      this.targetZoom = CAMERA_ZOOM_HOME_ENTER + (CAMERA_ZOOM_DEFAULT - CAMERA_ZOOM_HOME_ENTER) * smoothT;
    }

    // ── Enforce zoom floor (AGE-1178) ──
    if (this.targetZoom < CAMERA_ZOOM_MIN) {
      this.targetZoom = CAMERA_ZOOM_MIN;
    }

    // Smooth lerp towards target — use slower lerp when travel zoom is active
    const activeLerp = this.travelBlend > 0.01 ? CAMERA_TRAVEL_ZOOM_LERP_SPEED : CAMERA_ZOOM_LERP_SPEED;
    const lerpFactor = 1 - Math.pow(1 - activeLerp, delta / 16.667);
    this.currentZoom += (this.targetZoom - this.currentZoom) * lerpFactor;

    // Snap when very close to avoid perpetual micro-lerp
    if (Math.abs(this.currentZoom - this.targetZoom) < 0.0005) {
      this.currentZoom = this.targetZoom;
    }

    // Enforce floor on the final applied zoom as well
    if (this.currentZoom < CAMERA_ZOOM_MIN) {
      this.currentZoom = CAMERA_ZOOM_MIN;
    }

    this.camera.setZoom(this.currentZoom);
  }

  /** Force-set zoom (e.g. on zone transition). Resets lerp and travel state. */
  resetZoom(zoom?: number): void {
    this.currentZoom = zoom ?? CAMERA_ZOOM_DEFAULT;
    this.targetZoom = this.currentZoom;
    this.camera.setZoom(this.currentZoom);
    // Reset travel tracking so zone transitions don't carry stale velocity
    this.prevCartX = NaN;
    this.prevCartY = NaN;
    this.travelTimeSecs = 0;
    this.settleTimeSecs = 0;
    this.travelBlend = 0;
  }

  /**
   * Trigger the home-entry settle-zoom: briefly ease from 1.1 → 1.0.
   * Called when the player enters their home instance (AGE-1178).
   */
  settleToHome(): void {
    this.currentZoom = CAMERA_ZOOM_HOME_ENTER;
    this.targetZoom = CAMERA_ZOOM_HOME_ENTER;
    this.camera.setZoom(CAMERA_ZOOM_HOME_ENTER);
    this.homeSettleRemaining = CAMERA_HOME_SETTLE_SECS;
    // Reset travel tracking so home entry feels clean
    this.prevCartX = NaN;
    this.prevCartY = NaN;
    this.travelTimeSecs = 0;
    this.settleTimeSecs = 0;
    this.travelBlend = 0;
  }

  /** Current effective zoom level. */
  getZoom(): number {
    return this.currentZoom;
  }

  /** Current target zoom (what we're lerping towards). */
  getTargetZoom(): number {
    return this.targetZoom;
  }
}
